import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";

// Components
import DynamicTabs from "../../components/Viewproduct/Tabs/index.jsx";
import VerticalCarousel from "../../components/Viewproduct/Carousel/Index.jsx";
import HorizontalCarousel from "../../components/common/productcarouselComponent/Index.jsx";
import Spinner from "../../components/common/loadingSpinnercomponent/Spinner.jsx";

// Assets
import shoppingBagImage from "../../assests/shopping-bag 1.png";
import Mintpay from "../../assests/ProductCard/Mintpay.png";
import Koko from "../../assests/ProductCard/Koko.png";

// Services & Config
import { fetchProductBySlug, checkSalePageStatus } from "../../Services/ProductServices";
import { Image_URL, Related_Products_URL } from "../../config/config";

// Redux Actions
import { addToCart } from "../../slices/CartSlice";
import { toggleWishlistProduct } from "../../slices/wishlistSlice";

// Contexts
import { useCurrency } from "../../contexts/CurrencyContext";

// Styles
import "./ViewProduct.css";
import "react-toastify/dist/ReactToastify.css";

const ViewProduct = () => {
  const { slug } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const wishlistSlugs = useSelector((state) => state.wishlist?.wishlistSlugs || []);

  const { getCurrencySymbol, isSriLanka } = useCurrency();

  // State Management
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedSizeId, setSelectedSizeId] = useState("");
  const [selectedColorId, setSelectedColorId] = useState("");
  const [selectedPCID, setSelectedPCID] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [showSizeChart, setShowSizeChart] = useState(false);
  const [mainImage, setMainImage] = useState("");
  const [colorImages, setColorImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaleEnabled, setIsSaleEnabled] = useState(false);

  // Sale status check
  useEffect(() => {
    const checkSaleStatus = async () => {
      try {
        const status = await checkSalePageStatus();
        setIsSaleEnabled(status.isEnabled);
      } catch (error) {
        console.error('Error checking sale status:', error);
        setIsSaleEnabled(false);
      }
    };
    checkSaleStatus();
  }, []);

  // Initial Load & Product Fetching
  useEffect(() => {
    const fetchProductDetails = async () => {
      setIsLoading(true);
      try {
        const productDetails = await fetchProductBySlug(slug);

        if (!productDetails || !Array.isArray(productDetails) || !productDetails[0]) {
          throw new Error('Product not found');
        }

        const fetchedProduct = productDetails[0];
        fetchedProduct.Slug = fetchedProduct.Slug || fetchedProduct.slug;

        setProduct(fetchedProduct);

        // Initialize with first available size and color
        if (fetchedProduct.Colors && fetchedProduct.Colors.length > 0) {
          // Find first available size (with stock)
          const availableSizes = fetchedProduct.Colors
            .filter(color => parseInt(color.Qty) > 0)
            .map(color => color.Size);

          const firstAvailableSize = availableSizes[0];

          if (firstAvailableSize) {
            // Find the first color variant for this size
            const firstColorForSize = fetchedProduct.Colors.find(
              color => color.Size === firstAvailableSize && parseInt(color.Qty) > 0
            );

            if (firstColorForSize) {
              setSelectedSize(firstAvailableSize);
              setSelectedSizeId(firstColorForSize.SizeId);
              handleColorClick(firstColorForSize);
            }
          }
        }

        fetchRelatedProducts(
          fetchedProduct.CategoryId,
          fetchedProduct.SubCategoryId,
          fetchedProduct.CollectionId
        );
      } catch (error) {
        console.error("Error fetching product details:", error);
        setError(error);
        toast.error("Error loading product details");
      } finally {
        setIsLoading(false);
      }
    };
    fetchProductDetails();
  }, [slug]);

  // Related Products Fetching
  const fetchRelatedProducts = async (categoryId, subcategoryId, collectionId) => {
    try {
      const response = await axios.get(
        `${Related_Products_URL}?categoryId=${categoryId}&subcategoryId=${subcategoryId}&collectionId=${collectionId}`
      );
      if (response.data.status === 1) {
        setRelatedProducts(response.data.data);
      } else {
        console.error("Error fetching related products:", response.data.message);
      }
    } catch (error) {
      console.error("Error fetching related products:", error);
    }
  };

  // Helper Functions
  const handleSizeSelect = (size) => {
    if (!product || !product.Colors) return;

    // Only allow selecting a new size, not deselecting
    if (size !== selectedSize) {
      const sizeVariant = product.Colors.find(color => color.Size === size);
      setSelectedSize(size);
      setSelectedSizeId(sizeVariant.SizeId);

      // Handle color selection for new size
      const availableColors = product.Colors.filter(
        (color) => color.Size === size && parseInt(color.Qty) > 0
      );

      const colorMatch = availableColors.find(
        (color) => color.Color === selectedColor
      );

      if (colorMatch) {
        handleColorClick(colorMatch);
      } else if (availableColors.length > 0) {
        handleColorClick(availableColors[0]);
      }
    }
  };

  const handleColorClick = (color) => {
    const images = color.Sources.split(",").map((source) => source.trim());
    const firstImage = images[0];
    setSelectedColor(color.Color);
    setSelectedColorId(color.ColorId);
    setSelectedPCID(color.PCID);
    setMainImage(firstImage);
    setColorImages(images);
  };

  const handleQuantityChange = (event) => {
    const value = parseInt(event.target.value, 10);
    if (value > 0) {
      setQuantity(value);
    }
  };

  const getUniqueSizes = (colors) => {
    if (!colors || colors.length === 0) return [];
    const uniqueSizes = new Set();
    colors.forEach((color) => {
      if (parseInt(color.Qty) > 0) { // Only add sizes that are in stock
        uniqueSizes.add(color.Size);
      }
    });
    return Array.from(uniqueSizes);
  };

  const uniqueColorsForSize = (selectedSize) => {
    if (!product || !product.Colors) return [];
    const colorSet = new Set();
    const uniqueColors = product.Colors.filter(
      (color) =>
        (selectedSize ? color.Size === selectedSize : true) &&
        parseInt(color.Qty) > 0 && // Only show colors that are in stock
        !colorSet.has(color.Color) &&
        colorSet.add(color.Color)
    );
    return uniqueColors;
  };

  const handleSizeChartClick = () => {
    setShowSizeChart(!showSizeChart);
  };

  const handleCardClick = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // Get display prices
  const getDisplayPrices = () => {
    if (!product) return { currentPrice: 0, originalPrice: null };

    if (isSriLanka()) {
      // For Sri Lankan users
      if (isSaleEnabled && product.OfferPrice && parseFloat(product.OfferPrice) > 0) {
        return {
          currentPrice: parseFloat(product.OfferPrice),
          originalPrice: parseFloat(product.Price)
        };
      }
      return {
        currentPrice: parseFloat(product.Price),
        originalPrice: null
      };
    } else {
      // For International users
      if (isSaleEnabled && product.InternationalOfferPrice && parseFloat(product.InternationalOfferPrice) > 0) {
        return {
          currentPrice: parseFloat(product.InternationalOfferPrice),
          originalPrice: parseFloat(product.InternationalPrice || product.Price)
        };
      }
      return {
        currentPrice: parseFloat(product.InternationalPrice || product.Price),
        originalPrice: null
      };
    }
  };

  // Add to Cart Handler
  const handleSubmit = (event) => {
    event.preventDefault();

    if (!selectedSize) {
      toast.error("Please select a size");
      return;
    }

    if (!selectedColor) {
      toast.error("Please select a color");
      return;
    }

    const selectedVariant = product.Colors.find(
      variant =>
        variant.Size === selectedSize &&
        variant.Color === selectedColor
    );

    if (!selectedVariant) {
      toast.error("Selected combination is not available");
      return;
    }

    if (parseInt(selectedVariant.Qty) <= 0) {
      toast.error("Selected variant is out of stock");
      return;
    }

    const { currentPrice } = getDisplayPrices();

    const productToAdd = {
      ProductId: parseInt(product.ProductId),
      Title: product.Title,
      Color: selectedColor,
      ColorId: selectedColorId,
      Size: selectedSize,
      SizeId: selectedSizeId,
      PCID: selectedPCID,
      quantity: quantity,
      Image: mainImage,
      Slug: product.Slug,
      Price: product.Price,
      InternationalPrice: product.InternationalPrice,
      OfferPrice: product.OfferPrice,
      InternationalOfferPrice: product.InternationalOfferPrice,
      currentPrice: currentPrice,
      isOnSale: isSaleEnabled,
      originalPrice: product.Price
    };

    dispatch(addToCart(productToAdd));
    toast.success("Product added to cart!");
  };

  // Wishlist Toggle Handler
  const toggleWishlist = () => {
    if (!product?.Slug) return;
    dispatch(toggleWishlistProduct(product.Slug));

    const isCurrentlyWishlisted = wishlistSlugs.includes(product.Slug);
    toast.success(isCurrentlyWishlisted
      ? "Removed from wishlist"
      : "Added to wishlist"
    );
  };

  // Loading State
  if (isLoading) {
    return <Spinner />;
  }

  // Error State
  if (error) {
    return (
      <div className="view-product-error">
        Error loading product: {error.message}
      </div>
    );
  }

  // Not Found State
  if (!product) {
    return <div className="view-product-error">Product not found</div>;
  }

  const uniqueSizes = getUniqueSizes(product.Colors);
  const isWishlisted = wishlistSlugs.includes(product?.Slug);
  const currencySymbol = getCurrencySymbol();
  const { currentPrice, originalPrice } = getDisplayPrices();
  const installmentPrice = (currentPrice / 3).toFixed(2);

  // Main Render
  return (
    <>
      <div className="view-product-container">
        <ToastContainer />
        <form className="view-product-wrapper" onSubmit={handleSubmit}>
          <div className="product-mobile-only">
            <h1>{product.Title}</h1>
          </div>

          {/* Product Images Section */}
          <div className="view-product-image-wrapper">
            <VerticalCarousel
              images={colorImages}
              setMainImage={setMainImage}
            />
            <div className="view-product-main-image">
              {mainImage && (
                <img
                  src={`${Image_URL}products/thumbnails/${mainImage}`}
                  alt={product.Title}
                  onClick={handleCardClick}
                  onError={(e) => {
                    e.target.src = "https://jbadmin.alephaz.com/assets/img/placeholder-image.jpg";
                  }}
                />
              )}
              <div
                className="view-product-heart-icon-container"
                onClick={toggleWishlist}
              >
                {isWishlisted ? (
                  <i className="fa-solid fa-heart" style={{ color: "red" }}></i>
                ) : (
                  <i className="fa-regular fa-heart"></i>
                )}
              </div>
            </div>
          </div>

          {/* Product Details Section */}
          <div className="view-product-details">
            <h1>{product.Title}</h1>
            <p className="view-product-description">{product.Description}</p>

            {/* Price Display */}
            <div className="view-product-price-container">
              {originalPrice ? (
                <>
                  <p className="view-product-original-price">
                    {currencySymbol} {originalPrice.toFixed(2)}
                  </p>
                  <p className="view-product-offer-price">
                    {currencySymbol} {currentPrice.toFixed(2)}
                  </p>
                </>
              ) : (
                <p className="view-product-price">
                  {currencySymbol} {currentPrice.toFixed(2)}
                </p>
              )}
            </div>

            {/* Installment Options */}
            <div className="view-product-installment">
              <p>
                or 3 installments of {currencySymbol} {installmentPrice} with
              </p>
              <div className="view-product-payment-logos-container">
                <img
                  src={Mintpay}
                  alt="MintPay"
                  className="view-product-payment-logo me-2"
                />
                <p className="view-product-installments">or</p>
                <img
                  src={Koko}
                  alt="Koko"
                  className="view-product-payment-logo"
                />
              </div>
            </div>

            {/* Size Selection */}
            <p className="view-product-size-label">Size:</p>
            <div className="view-product-size-options">
              {uniqueSizes.map((size) => {
                const sizeOption = product.Colors.find(
                  (color) => color.Size === size
                );
                const isOutOfStock = sizeOption && parseInt(sizeOption.Qty) === 0;
                return (
                  <button
                    type="button"
                    key={size}
                    onClick={() => handleSizeSelect(size)}
                    className={`view-product-size-options button ${
                      selectedSize === size ? "view-product-selected" : ""
                    }`}
                    disabled={isOutOfStock}
                    title={isOutOfStock ? "Out of stock" : ""}
                  >
                    {size}
                  </button>
                );
              })}
            </div>

            {/* Size Chart */}
            <p
              className="view-product-size-chart-toggle"
              onClick={handleSizeChartClick}
            >
              {showSizeChart ? "Hide Size Chart" : "View Size Chart"}
            </p>
            {showSizeChart && (
              <div className="view-product-size-chart">
                <img
                  src="https://www.glamly.com/cdn/shop/articles/MicrosoftTeams-image_46_1200x1200.png?v=1698044645"
                  alt="Size Chart"
                  className="view-product-size-chart-image"
                />
              </div>
            )}

            {/* Color Selection */}
            <div className="view-product-color-dots-container">
              {uniqueColorsForSize(selectedSize).map((color, index) => (
                <div
                  key={index}
                  className={`view-product-color-dot ${
                    selectedColor === color.Color ? "view-product-selected" : ""
                  }`}
                  style={{ backgroundColor: color.Color }}
                  title={color.Color}
                  onClick={() => handleColorClick(color)}
                ></div>
              ))}
            </div>

            {/* Quantity and Add to Cart */}
            <div className="view-product-quantity-cart">
              <div className="view-product-quantity-counter">
                <button
                  type="button"
                  className="view-product-quantity-btn-left"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                >
                  <i className="fa-solid fa-minus"></i>
                </button>
                <input
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={handleQuantityChange}
                  className="view-product-quantity-field"
                />
                <button
                  type="button"
                  className="view-product-quantity-btn"
                  onClick={() => setQuantity(quantity + 1)}
                >
                  <i className="fa-solid fa-plus"></i>
                </button>
              </div>
              <button type="submit" className="view-product-add-to-cart">
                <img
                  src={shoppingBagImage}
                  alt="shopping bag"
                  className="view-product-shopping-bag-icon"
                />
                <p>Add to Cart</p>
              </button>
            </div>
          </div>
        </form>

        {/* Product Tabs Section */}
        {product && (
          <DynamicTabs product={product} productId={product.ProductId} />
        )}

        {/* Similar Products Section */}
        <h2 className="view-product-similar-products">Similar Products</h2>
        
          <HorizontalCarousel
            products={relatedProducts}
            onMainImageClick={handleCardClick}
          />
      </div>
    </>
  );
};

export default ViewProduct;